import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";

import { ApiError, auth } from "~/lib/api";
import { AuthShell, Field, inputCls, primaryBtn } from "./auth.sign-in";

export const Route = createFileRoute("/auth/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await auth.forgotPassword(email);
      setDone(true);
    } catch (err) {
      // Backend always returns 204 — only network-level failures land here.
      setError(
        err instanceof ApiError ? err.message : "Could not send reset email",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthShell
        title="Check your email"
        subtitle="If an account exists for that address, we sent a reset link."
      >
        <p className="text-sm text-[color:var(--color-abyssal)]/70">
          The link expires in 1 hour. Didn’t receive it? Check your spam folder,
          then try again.
        </p>
        <div className="mt-6 text-sm">
          <Link
            to="/auth/sign-in"
            className="text-[color:var(--color-north-star)] hover:underline"
          >
            Back to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle="We'll email you a link to set a new one."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            className={inputCls}
          />
        </Field>

        {error ? (
          <p className="text-sm text-[color:var(--color-coral)]">{error}</p>
        ) : null}

        <button type="submit" disabled={submitting} className={primaryBtn}>
          {submitting ? "Sending…" : "Send reset link"}
        </button>

        <p className="text-center text-sm">
          <Link
            to="/auth/sign-in"
            className="text-[color:var(--color-north-star)] hover:underline"
          >
            Back to sign in
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
